// "Here's what you'll get" — the web twin of ios PrintMockup.swift.
//
// Every product composites the portrait into a real studio product photo at a
// print rect expressed in normalised [0,1] coords of the (square) base photo,
// multiply-blended so the design picks up the material's shading, folds and
// curvature and reads as printed rather than pasted on. The rects and blend
// rules below are lifted verbatim from the iOS component so the two surfaces
// show the same thing; if you change one, change the other.
//
// (My first pass rendered `product.mockup` — a field the catalog has never
// returned — so nothing ever appeared. The catalog gives `category`, which is
// what actually maps to a mockup.)

const ART = "https://pub-d5b5c6e83c154b23886e1734d75d978d.r2.dev/mockups";

interface Rect { x: number; y: number; w: number; h: number }
interface Spec {
  asset: string;
  print: Rect;
  /** design corner radius as a fraction of print width */
  corner?: number;
  /** mug: shade the design edges to fake the cylinder */
  curved?: boolean;
  /** dark garments need normal blend — multiply would erase the design */
  blend?: "multiply" | "normal";
}

/** Tee garment colour → base photo (+ whether the fabric is dark). */
function tee(colorId?: string): { asset: string; dark: boolean } {
  switch (colorId) {
    case "black": return { asset: "mockup_tee_black", dark: true };
    case "navy": return { asset: "mockup_tee_navy", dark: true };
    case "athletic-heather": return { asset: "mockup_tee_heather", dark: false };
    default: return { asset: "mockup_tee_white", dark: false };
  }
}

function specFor(category: string, colorId?: string): Spec {
  switch (category) {
    case "print":        return { asset: "mockup_print", print: { x: 0.265, y: 0.155, w: 0.47, h: 0.71 } };
    case "canvas":       return { asset: "mockup_canvas", print: { x: 0.23, y: 0.13, w: 0.545, h: 0.745 } };
    case "framed_print": return { asset: "mockup_framed", print: { x: 0.295, y: 0.205, w: 0.41, h: 0.58 } };
    case "acrylic":      return { asset: "mockup_acrylic", print: { x: 0.28, y: 0.18, w: 0.44, h: 0.65 }, corner: 0.02 };
    case "wood":         return { asset: "mockup_wood", print: { x: 0.195, y: 0.09, w: 0.61, h: 0.82 } };
    case "mug":          return { asset: "mockup_mug", print: { x: 0.275, y: 0.31, w: 0.275, h: 0.42 }, curved: true };
    case "tote":         return { asset: "mockup_tote", print: { x: 0.36, y: 0.485, w: 0.28, h: 0.335 } };
    case "phone_case":   return { asset: "mockup_phonecase", print: { x: 0.36, y: 0.19, w: 0.295, h: 0.66 }, corner: 0.06 };
    case "apparel": {
      const t = tee(colorId);
      return { asset: t.asset, print: { x: 0.378, y: 0.30, w: 0.244, h: 0.34 }, blend: t.dark ? "normal" : "multiply" };
    }
    default:             return { asset: "mockup_print", print: { x: 0.265, y: 0.155, w: 0.47, h: 0.71 } };
  }
}

export function PrintMockup({
  category,
  portraitUrl,
  colorId,
  className = "",
}: {
  category: string;
  portraitUrl?: string | null;
  colorId?: string;
  className?: string;
}) {
  const spec = specFor(category, colorId);
  const pct = (n: number) => `${n * 100}%`;

  return (
    <div className={`relative aspect-square w-full overflow-hidden rounded bg-screenInk ${className}`}>
      <img src={`${ART}/${spec.asset}.png`} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      {portraitUrl && (
        <div
          className="absolute overflow-hidden"
          style={{
            left: pct(spec.print.x),
            top: pct(spec.print.y),
            width: pct(spec.print.w),
            height: pct(spec.print.h),
            // Fraction of the PRINT WIDTH, matching the iOS maths.
            borderRadius: spec.corner ? `${spec.corner * spec.print.w * 100}%` : undefined,
          }}
        >
          <img
            src={portraitUrl}
            alt="Your portrait on this product"
            className="h-full w-full object-cover"
            style={{ mixBlendMode: spec.blend === "normal" ? "normal" : "multiply" }}
          />
          {/* Mug: fake the cylinder by darkening the design's left/right edges. */}
          {spec.curved && (
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "linear-gradient(90deg, rgba(0,0,0,.35) 0%, rgba(0,0,0,0) 18%, rgba(0,0,0,0) 82%, rgba(0,0,0,.35) 100%)",
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
